/**
 * A report is a fixed template filled with two kinds of content: values that come straight
 * from the comparison (which areas each policy covers) and prose AL writes about them. The
 * `// AI` fields are the ones a model writes; the preview marks them so a reviewer can tell
 * which statements to check before the report goes to a client or a board.
 *
 * Every per-policy list is in the order of `policies`, so a report can compare any number
 * of policies.
 */
export interface IReportPolicy {
  /** Short column heading, e.g. the insurer's name. */
  label: string
  insurer: string
  product: string
  /** The wording PDF's path under the media URL, where the report links to its pages. */
  wordingPath?: string
}

/**
 * One policy difference as the wordings state it — the policy evidence. The optional terms
 * are the same provisions in a form a tailored report can calculate with; a null means that
 * policy's wording sets no figure (e.g. "as shown in the Schedule").
 */
export interface IReportFinding {
  /** The PolicyComparison feature, as the client-context methodology names it. */
  feature: string
  values: string[]
  /** Limit as a percentage of the Building Sum Insured. */
  percentOfBuildingSum?: (number | null)[]
  /** Limit as a dollar amount. */
  limitAmount?: (number | null)[]
}

export interface IReportContent {
  classOfCover: string
  policies: IReportPolicy[]
  conclusion: string // AI
  assessment: { area: string; preferred: string; reason: string }[] // AI
  similarities: { area: string; values: string[] }[]
  similarityBullets: string[] // AI
  boardObservation: string // AI
  /** Short "label: what differs" lines. */
  keyDifferences: { label: string; text: string }[] // AI
  /** Feature-by-feature values, each with which policy comes out ahead where it is assessed. */
  differenceTable: {
    topic: string
    values: string[]
    /** The page of each policy's wording its value is on, where the API gives one. */
    pages?: (string | null)[]
    assessment?: string
  }[]
  differencesNarrative: string // AI
  risks: { label: string; text: string }[] // AI
  recommendation: string[] // AI
  /** Differences a tailored report can quantify, contextualise or weigh for relevance. */
  findings?: IReportFinding[]
  /** The client details a tailored report was generated with. Edits made since apply only
   * once the report is generated again. */
  clientContext?: ClientContext
}

export enum ReportType {
  SUMMARY = 'summary',
  BOARD = 'board',
  TAILORED = 'tailored',
  /** Two policies only: what differs between them, area by area. */
  KEY_DIFFERENCES = 'keyDifferences',
}

export enum ReportSection {
  EXECUTIVE_SUMMARY = 'report-screen',
  POLICIES = 'policies',
  CLIENT_CONTEXT = 'client-context',
  SIMILARITIES = 'similarities',
  DIFFERENCES = 'differences',
  RISKS = 'risks',
  RECOMMENDATION = 'recommendation',
}

/** What the admin picks in the report settings. */
export interface IReportOptions {
  type: ReportType
}

/** The page layout a report type resolves to. */
export interface IReportLayout {
  type: ReportType
  sections: ReportSection[]
  /** Brief sections suit a one-page summary; full ones carry the tables and narrative. */
  detail: 'brief' | 'full'
  cover: boolean
  contents: boolean
  newPagePerSection: boolean
}

/**
 * Who issues the report. Set once in the report settings and used by every report type —
 * front sheet, sign-off and the PDF's running header and footer.
 */
export interface IReportDetails {
  /** Defaults to the signed-in user's name when left blank. */
  preparedBy: string
  role: string
  organisation: string
  /** Email or phone, as the issuer wants it shown. */
  contact: string
  /** The broker's own file or job reference. */
  reference: string
}

/** Client information a tailored report can use. */
export type ClientContextKey =
  | 'buildingSumInsured'
  | 'weeklyRent'
  | 'petsPermitted'
  | 'propertyUse'
  | 'floodExposure'

export type ClientContext = Partial<Record<ClientContextKey, number | string>> & {
  /** Free text from the broker about the client's circumstances; printed as given, and never
   * used to tailor a difference. */
  specialConditions?: string
}
